// skipcq: JS-W1028, JS-0128
import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Container,
  CircularProgress,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import Alert from '@mui/material/Alert';
import { isSafeHttpUrlForLink } from '../../domain/eventDefinitionValidation';
import { formatEventDateTimeLabel } from '../../domain/eventTimezones';
import {
  resolveStoredImageUrl,
  eventCardImageFilename,
} from './eventImageUpload';

function EventDefinitionsToolbar({ loading, onOpenAdd }) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 2,
        mb: 2,
        width: '100%',
      }}
    >
      <Button
        variant="contained"
        onClick={onOpenAdd}
        sx={{ ml: 'auto' }}
        disabled={loading}
      >
        Add event
      </Button>
    </Box>
  );
}

const eventCardLogoColumnSx = {
  width: { xs: 96, sm: 120 },
  minHeight: 96,
  flexShrink: 0,
  bgcolor: 'action.hover',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  p: 1,
  borderRight: 1,
  borderColor: 'divider',
};

function EventCardLogoColumn({ logoUrl, eventTitle }) {
  return (
    <Box sx={eventCardLogoColumnSx}>
      {logoUrl ? (
        <Box
          component="img"
          src={logoUrl}
          alt={eventTitle || 'Event'}
          referrerPolicy="no-referrer"
          sx={{
            maxHeight: 112,
            maxWidth: '100%',
            width: 'auto',
            height: 'auto',
            objectFit: 'contain',
          }}
        />
      ) : (
        <Typography variant="caption" color="text.secondary" align="center">
          No logo
        </Typography>
      )}
    </Box>
  );
}

const eventCardDetailsOuterSx = {
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
};

const eventCardContentSx = {
  flex: '1 1 auto',
  minWidth: 0,
  py: 1.5,
  px: 2,
  '&:last-child': { pb: 1 },
};

function EventCardDetailsColumn({
  host,
  name,
  dateLabel,
  location,
  linkTrimmed,
  linkTextTrimmed,
  onView,
}) {
  const ticketHref =
    linkTrimmed && isSafeHttpUrlForLink(linkTrimmed) ? linkTrimmed : '';

  return (
    <Box sx={eventCardDetailsOuterSx}>
      <CardContent sx={eventCardContentSx}>
        <Typography variant="h6" component="h3" gutterBottom>
          {name || 'Untitled'}
        </Typography>
        {host ? (
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {host}
          </Typography>
        ) : null}
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {dateLabel || '—'}
        </Typography>
        {location ? (
          <Typography variant="body2" sx={{ mb: 1 }}>
            {location}
          </Typography>
        ) : null}
        {ticketHref ? (
          <Link
            href={ticketHref}
            target="_blank"
            rel="noopener noreferrer"
            variant="body2"
            sx={{
              wordBreak: 'break-word',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
            title={ticketHref}
          >
            {linkTextTrimmed || ticketHref}
          </Link>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No link
          </Typography>
        )}
      </CardContent>
      <CardActions sx={{ justifyContent: 'flex-end', pt: 0, px: 1, pb: 1, mt: 'auto' }}>
        <Button size="small" onClick={onView}>
          View
        </Button>
      </CardActions>
    </Box>
  );
}

const eventDefinitionCardSx = {
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
};

const eventDefinitionCardRowSx = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'stretch',
  flex: 1,
  minHeight: 0,
};

function EventGeolocationAside({ latitude, longitude }) {
  const latStr = String(latitude ?? '').trim();
  const lngStr = String(longitude ?? '').trim();
  const hasAny = Boolean(latStr || lngStr);

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        gap: 1,
        pl: { xs: 0, sm: 2 },
        py: { xs: 1.5, sm: 0 },
        borderLeft: { xs: 0, sm: 1 },
        borderTop: { xs: 1, sm: 0 },
        borderColor: 'divider',
      }}
    >
      {!hasAny ? (
        <Typography variant="caption" color="text.disabled">
          No coordinates
        </Typography>
      ) : (
        <Stack spacing={1.25}>
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              Latitude
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}
            >
              {latStr || '—'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              Longitude
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}
            >
              {lngStr || '—'}
            </Typography>
          </Box>
        </Stack>
      )}
    </Box>
  );
}

function EventDefinitionCard({ event, imageUrlByName }) {
  const navigate = useNavigate();
  const handleView = useCallback(() => {
    if (!event?.id) return;
    navigate(`/events/${event.id}`);
  }, [navigate, event?.id]);

  const logoUrl = resolveStoredImageUrl(
    imageUrlByName,
    eventCardImageFilename(event),
  );
  const dateLabel = formatEventDateTimeLabel(
    event.eventLocalDate,
    event.eventTimezone,
  );
  const linkTrimmed = String(event.link ?? '').trim();
  const linkTextTrimmed = String(event.linkText ?? '').trim();
  const venueLine = String(event.location ?? '').trim();
  const cityLine = String(event.cityState ?? '').trim();
  const legacyVenueName = String(event.venueName ?? '').trim();
  const locationSummary =
    [venueLine || legacyVenueName, cityLine].filter(Boolean).join(' · ') || '';

  return (
    <Card variant="outlined" sx={eventDefinitionCardSx}>
      <Box sx={eventDefinitionCardRowSx}>
        <EventCardLogoColumn
          logoUrl={logoUrl}
          eventTitle={event.name || 'Event'}
        />
        <EventCardDetailsColumn
          host={String(event.host ?? '').trim()}
          name={event.name}
          dateLabel={dateLabel}
          location={locationSummary}
          linkTrimmed={linkTrimmed}
          linkTextTrimmed={linkTextTrimmed}
          onView={handleView}
        />
      </Box>
    </Card>
  );
}

/**
 * Event definitions tab: add action and card grid.
 */
function EventDefinitionsTab({
  loading,
  error,
  onOpenAdd,
  events,
  imageUrlByName,
}) {
  const tableSection = loading ? (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        py: 4,
      }}
    >
      <CircularProgress />
    </Box>
  ) : (
    <>
      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}
      {events.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
          No events yet. Add one with the button above, or upload images on the Event images tab first.
        </Typography>
      ) : (
        <Stack spacing={2}>
          {events.map((ev) => (
            <Box
              key={ev.id}
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'stretch', sm: 'stretch' },
                gap: 0,
              }}
            >
              <Box
                sx={{
                  flex: { xs: '1 1 auto', sm: '0 0 50%' },
                  width: { xs: '100%', sm: '50%' },
                  maxWidth: { xs: '100%', sm: '50%' },
                  minWidth: 0,
                }}
              >
                <EventDefinitionCard
                  event={ev}
                  imageUrlByName={imageUrlByName}
                />
              </Box>
              <Box
                sx={{
                  flex: { xs: '1 1 auto', sm: '0 0 50%' },
                  width: { xs: '100%', sm: '50%' },
                  maxWidth: { xs: '100%', sm: '50%' },
                  minWidth: 0,
                }}
              >
                <EventGeolocationAside
                  latitude={ev.latitude}
                  longitude={ev.longitude}
                />
              </Box>
            </Box>
          ))}
        </Stack>
      )}
    </>
  );

  return (
    <Container maxWidth="lg" sx={{ mt: 2, mb: 4 }}>
      <EventDefinitionsToolbar loading={loading} onOpenAdd={onOpenAdd} />
      {tableSection}
    </Container>
  );
}

export default EventDefinitionsTab;
